import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { usePathname } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../../context/AuthContext';
import { useTier } from '../../context/TierContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useRealtimeRecommendations } from '../../hooks/useRealtimeRecommendations';
import { useDormantSignals } from '../../hooks/useDormantSignals';
import { useHomeSurveyActive } from '../survey/surveyVisibility';

/**
 * D-033 (desktop): floating side card that hoists the "Models on hold" row
 * out of the feed footer and anchors it bottom-right, above the existing
 * DesktopProFloatingCard upgrade card. Only renders on /home, desktop, Free
 * users with at least one dormant+selected model. Mobile keeps the in-feed
 * footer section.
 *
 * Tap on any tile fires dormant_model_card_tapped + triggerUpgradeModal
 * with source 'dormant_card' (same contract as the in-feed DormantModelCard).
 */
const DesktopDormantModelsCard = () => {
  const { isMobile } = useResponsive();
  const pathname = usePathname();
  const { session } = useAuth();
  const { hasProAccess, triggerUpgradeModal } = useTier();
  const posthog = usePostHog();
  const surveyActive = useHomeSurveyActive();

  const { recommendations } = useRealtimeRecommendations(session, 'batch');
  const dormantModels = useMemo(
    () => (hasProAccess ? [] : recommendations.filter((r) => r.dormant_at && r.selected_for_feed)),
    [hasProAccess, recommendations]
  );
  const signals = useDormantSignals({
    userId: session?.user?.id,
    hasProAccess,
    enabled: dormantModels.length > 0,
  });

  // Held back during the /home survey sequence (blank 8s -> survey -> upgrade).
  if (isMobile || hasProAccess || pathname !== '/home' || dormantModels.length === 0 || surveyActive) return null;

  const handleTap = (model) => {
    posthog?.capture('dormant_model_card_tapped', {
      model_id: model?.modelId,
      make: model?.make,
      model_name: model?.model,
      surface: 'desktop_floating_card',
    });
    triggerUpgradeModal?.({ source: 'dormant_card' });
  };

  return (
    <View style={styles.anchor} pointerEvents="box-none">
      <View style={styles.card}>
        {Platform.OS !== 'web' && (
          <LinearGradient
            colors={['rgba(255, 181, 71, 0.12)', 'rgba(255, 181, 71, 0.04)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={StyleSheet.absoluteFill}
            pointerEvents="none"
          />
        )}
        <View style={styles.head}>
          <MaterialCommunityIcons name="clock-outline" size={12} color="#FFB547" />
          <Text style={styles.headText}>Models on hold</Text>
        </View>
        <Text style={styles.sub}>Activity on your other tracked models while they're paused.</Text>
        <View style={styles.stack}>
          {dormantModels.map((m) => {
            const signalLine = signals[m.modelId] || null;
            return (
              <Pressable
                key={m.userModelFitScoreId ?? m.modelId}
                onPress={() => handleTap(m)}
                style={styles.tile}
                accessibilityRole="button"
                accessibilityLabel={`Activate ${m.make} ${m.model} with Pro`}
              >
                <View style={styles.tileTextCol}>
                  <Text style={styles.tileTitle} numberOfLines={1}>
                    {m.make} <Text style={styles.tileModel}>{(m.model || '').toUpperCase()}</Text>
                  </Text>
                  {signalLine ? (
                    <Text style={styles.tileSignal} numberOfLines={2}>{signalLine}</Text>
                  ) : (
                    <Text style={styles.tileFallback}>Activate with Pro</Text>
                  )}
                </View>
                <MaterialCommunityIcons name="chevron-right" size={18} color="rgba(255,255,255,0.5)" />
              </Pressable>
            );
          })}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  anchor: {
    ...(Platform.OS === 'web' ? { position: 'fixed' } : { position: 'absolute' }),
    // Stacks directly above DesktopProFloatingCard (bottom: 24, height ~260px + 12 gap).
    bottom: 300,
    right: 24,
    zIndex: 200,
    width: 300,
  },
  card: {
    position: 'relative',
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255, 181, 71, 0.22)',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.35,
    shadowRadius: 24,
    elevation: 10,
    ...(Platform.OS === 'web'
      ? {
          backgroundImage:
            'radial-gradient(200px 100px at 100% 0%, rgba(255,181,71,0.14), transparent 60%), linear-gradient(180deg, rgba(40,28,8,0.6) 0%, rgba(20,14,4,0.65) 100%)',
        }
      : { backgroundColor: 'rgba(20,14,4,0.65)' }),
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
    letterSpacing: 1,
    color: '#FFB547',
    textTransform: 'uppercase',
  },
  sub: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    lineHeight: 18,
    color: 'rgba(255,255,255,0.6)',
    marginTop: 6,
  },
  stack: {
    marginTop: 12,
    gap: 8,
  },
  tile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  tileTextCol: {
    flex: 1,
    minWidth: 0,
  },
  tileTitle: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12,
    color: 'rgba(255,255,255,0.75)',
  },
  tileModel: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
  },
  tileSignal: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
    lineHeight: 14,
    color: '#FFB547',
    marginTop: 4,
  },
  tileFallback: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 11,
    color: 'rgba(143, 179, 255, 0.9)',
    marginTop: 4,
  },
});

export default DesktopDormantModelsCard;
